import { createWebHistory, createRouter } from "vue-router";
import { useAuthStore } from "../pinia/authStore";
import { ROLES } from "@/components/Utilities/javascripts/permissions";
import VueCookies from "vue-cookies";

// offline
import OfflineAssessment from "../views/OfflineAssessment.vue";
import FormType1 from "../views/AuthUserRoutes/Form/FormType1.vue";
import FormType2 from "../views/AuthUserRoutes/Form/FormType2.vue";

// ---------- Base64URL helpers (unicode-safe) ----------
function b64uEncode(str) {
  const b64 = btoa(unescape(encodeURIComponent(str)));
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}
function b64uDecode(s) {
  let b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  while (b64.length % 4) b64 += "=";
  return decodeURIComponent(escape(atob(b64)));
}
function isB64u(s) {
  if (!/^[A-Za-z0-9\-_]+$/.test(s)) return false;
  try {
    return b64uEncode(b64uDecode(s)) === s;
  } catch {
    return false;
  }
}

const routes = [
  // Global routes
  {
    path: "/delete-account-success",
    component: () => import("../views/DeleteAccountSuccess.vue"),
    meta: {
      showFooter: true,
    },
  },
  {
    path: "/login",
    component: () => import("../views/GlobalRoutes/Login.vue"),
    meta: {
      showFooter: true,
    },
  },
  {
    path: "/register",
    component: () => import("../views/GlobalRoutes/Register.vue"),
    meta: {
      showFooter: true,
    },
  },
  {
    path: "/report-emergency",
    name: "ReportEmergency",
    component: () => import("../views/GlobalRoutes/ReportEmergency.vue"),
    meta: {
      showFooter: true,
    },
  },
  {
    path: "/forgot-password",
    component: () => import("../views/GlobalRoutes/ForgotPassword.vue"),
    meta: {
      showFooter: true,
    },
  },
  {
    path: "/change-password",
    component: () => import("../views/GlobalRoutes/ChangePassword.vue"),
    meta: {
      showFooter: true,
    },
  },
  {
    path: "/link-expired",
    component: () => import("../views/GlobalRoutes/LinkExpired.vue"),
    meta: {
      showFooter: true,
    },
  },
  {
    path: "/",
    component: () => import("../views/LandingPage.vue"),
  },
  {
    path: "/home-page",
    component: () => import("../views/HomePage.vue"),
  },
  {
    path: "/floods",
    component: () => import("../views/Flooding.vue"),
  },
  {
    path: "/landslides",
    component: () => import("../views/Landslide.vue"),
  },
  {
    path: "/earthquake",
    component: () => import("../views/Earthquake.vue"),
  },
  {
    path: "/wildfire",
    component: () => import("@/views/AuthUserRoutes/Disaster/Wildfire.vue"),
  },
  {
    path: "/air-pollution",
    component: () => import("../views/AirPollution.vue"),
  },
  {
    path: "/landslide",
    component: () => import("../views/Landslide.vue"),
  },
  {
    path: "/emergency-reports",
    component: () => import("../views/EmergencyReports.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/report-success",
    component: () => import("../views/GlobalRoutes/ReportSuccess.vue"),
  },
  {
    path: "/check-report-status",
    name: "CheckStatus",
    component: () => import("../views/GlobalRoutes/CheckStatus.vue"),
  },
  {
    path: "/check-report-status/:phone/:id",
    name: "CheckStatusDetail",
    component: () => import("../views/GlobalRoutes/CheckStatusDetail.vue"),
  },
  {
    path: "/public-relations-news",
    name: "public-relations-news",
    component: () => import("../views/AuthUserRoutes/PublicRelations.vue"),
    meta: {
      breadcrumb: "ข่าวประชาสัมพันธ์",
    },
  },
  {
    path: "/public-relations-news/:publicRelationsId",
    name: "public-relations-news-detail",
    component: () =>
      import("../views/AuthUserRoutes/PublicRelationsDetail.vue"),
    meta: {
      breadcrumb: (route) => decodeURIComponent(route.params.publicRelationsId),
      parent: "public-relations-news",
    },
  },

  // Auth user routes
  {
    path: "/dashboard",
    name: "dashboard",
    component: () => import("../views/AuthUserRoutes/Dashboard.vue"),
    meta: {
      breadcrumb: "แดชบอร์ด",
      requiresAuth: true,
      authUserRoutes: true,
    },
  },
  {
    path: "/subdashboard",
    component: () => import("../views/AuthUserRoutes/SubDashboard.vue"),
    meta: {
      requiresAuth: true,
      authUserRoutes: true,
    },
  },
  {
    path: "/dashboard/:publicRelationsId",
    name: "dashboard-public-relations-detail",
    component: () =>
      import("../views/AuthUserRoutes/PublicRelationsDetail.vue"),
    meta: {
      breadcrumb: (route) => decodeURIComponent(route.params.publicRelationsId),
      parent: "dashboard",
      requiresAuth: true,
      authUserRoutes: true,
    },
  },
  {
    path: "/disaster",
    meta: {
      requiresAuth: true,
      authUserRoutes: true,
    },
    children: [
      {
        path: "flood",
        name: "Disaster-Flood",
        component: () => import("../views/AuthUserRoutes/Disaster/Flood.vue"),
      },
      {
        path: "landslide",
        name: "Disaster-Landslide",
        component: () =>
          import("../views/AuthUserRoutes/Disaster/Landslide.vue"),
      },
      {
        path: "wildfire",
        name: "Disaster-Wildfire",
        component: () =>
          import("../views/AuthUserRoutes/Disaster/Wildfire.vue"),
      },
      {
        path: "smog",
        name: "Disaster-Smog",
        component: () => import("../views/AuthUserRoutes/Disaster/Smog.vue"),
      },
      {
        path: "earthquake",
        name: "Disaster-Earthquake",
        component: () =>
          import("../views/AuthUserRoutes/Disaster/Earthquake.vue"),
      },
      {
        path: "wildfire",
        name: "Wildfire",
        component: () =>
          import("../views/AuthUserRoutes/Disaster/Wildfire.vue"),
      },
      // {
      //   path: "summer-storm",
      //   name: "Disaster-SummerStorm",
      //   component: () =>
      //     import("../views/AuthUserRoutes/Disaster/makeNewPage.vue"),
      // },
      // {
      //   path: "epidemic",
      //   name: "Disaster-Epidemic",
      //   component: () =>
      //     import("../views/AuthUserRoutes/Disaster/makeNewPage.vue"),
      // },
      // {
      //   path: "fire",
      //   name: "Disaster-Fire",
      //   component: () =>
      //     import("../views/AuthUserRoutes/Disaster/makeNewPage.vue"),
      // },
      {
        path: "history",
        name: "Disaster-history",
        component: () =>
          import("../views/AuthUserRoutes/Disaster/DisasterHistory.vue"),
      },
    ],
  },
  //menu shelter
  {
    path: "/shelter",
    name: "shelter",
    component: () => import("../views/AuthUserRoutes/Shelter.vue"),
    meta: {
      breadcrumb: "ศูนย์พักพิง",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_SHELTER"],
    },
  },
  {
    path: "/shelter/:shelterId",
    name: "shelter-details",
    component: () => import("../views/AuthUserRoutes/ShelterDetails.vue"),
    meta: {
      breadcrumb: (route) => decodeURIComponent(route.params.shelterId),
      parent: "shelter",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_SHELTER"],
    },
  },
  {
    path: "/shelter/:shelterId/add-victim",
    name: "shelter-add-victim",
    component: () => import("../views/AuthUserRoutes/AddVictim.vue"),
    meta: {
      breadcrumb: "เพิ่มผู้ประสบภัย",
      parent: "shelter-details",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_SHELTER"],
    },
  },
  {
    path: "/shelter/:shelterId/add-caregiver",
    name: "add-caregiver",
    component: () => import("../views/AuthUserRoutes/AddCaregiver.vue"),
    meta: {
      breadcrumb: "เพิ่มผู้ดูแล",
      parent: "shelter-details",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_SHELTER"],
    },
  },
  {
    path: "/shelter/:shelterId/request-resources",
    name: "request-resources",
    component: () => import("../views/AuthUserRoutes/AddRequestResources.vue"),
    meta: {
      breadcrumb: "ร้องขอทรัพยากร",
      parent: "shelter-details",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_SHELTER"],
    },
  },
  {
    path: "/shelter/:shelterId/caregivers",
    name: "caregivers-in-shelter",
    component: () => import("../views/AuthUserRoutes/CaregiversInShelter.vue"),
    meta: {
      breadcrumb: "ผู้ดูแลประจำศูนย์",
      parent: "shelter-details",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_SHELTER"],
    },
  },
  {
    path: "/shelter/:shelterId/caregivers/add-caregiver",
    name: "add-caregiver-in-shelter",
    component: () => import("../views/AuthUserRoutes/AddCaregiver.vue"),
    meta: {
      breadcrumb: "เพิ่มผู้ดูแล",
      parent: "caregivers-in-shelter",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_SHELTER"],
    },
  },
  {
    path: "/shelter/:shelterId/caregivers/:caregiverId",
    name: "caregivers-detail",
    component: () => import("../views/AuthUserRoutes/CaregiverDetails.vue"),
    meta: {
      breadcrumb: (route) => decodeURIComponent(route.params.caregiverId),
      parent: "caregivers-in-shelter",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_SHELTER"],
    },
  },
  {
    path: "/shelter/:shelterId/victims",
    name: "victims-in-shelter",
    component: () => import("../views/AuthUserRoutes/VictimsInShelter.vue"),
    meta: {
      breadcrumb: "ผู้ประสบภัยภายในศูนย์",
      parent: "shelter-details",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_SHELTER"],
    },
  },
  {
    path: "/shelter/:shelterId/victims/add-victim",
    name: "shelter-victims-add-victim",
    component: () => import("../views/AuthUserRoutes/AddVictim.vue"),
    meta: {
      breadcrumb: "เพิ่มผู้ประสบภัย",
      parent: "victims-in-shelter",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_SHELTER"],
    },
  },
  {
    path: "/shelter/:shelterId/victims/:victimId",
    name: "victim-detail-in-shelter",
    component: () => import("../views/AuthUserRoutes/VictimDetails.vue"),
    meta: {
      breadcrumb: (route) => decodeURIComponent(route.params.victimId),
      parent: "victims-in-shelter",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_SHELTER"],
    },
  },
  {
    path: "/shelter/:shelterId/victims/:victimId/:populationFormId/:assessmentFormId/form1",
    name: "victim-detail-in-shelter-form1",
    component: () => import("../views/AuthUserRoutes/Form/FormType1.vue"),
    meta: {
      breadcrumb: "รายงานการปฏิบัติการฉุกเฉิน",
      parent: "victim-detail-in-shelter",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_SHELTER"],
    },
  },
  {
    path: "/shelter/:shelterId/victims/:victimId/:populationFormId/:assessmentFormId/form2",
    name: "victim-detail-in-shelter-form2",
    component: () => import("../views/AuthUserRoutes/Form/FormType2.vue"),
    meta: {
      breadcrumb: "แบบบันทึกเวชระเบียนของทีมปฏิบัติการฉุกเฉิน",
      parent: "victim-detail-in-shelter",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_SHELTER"],
    },
  },
  {
    path: "/shelter/:shelterId/victims/:victimId/:populationFormId/:assessmentFormId/form3",
    name: "victim-detail-in-shelter-form3",
    component: () =>
      import("../views/AuthUserRoutes/Form/FormType3_backup.vue"),
    meta: {
      breadcrumb:
        "แบบบันทึกเวชระเบียนของทีมปฏิบัติการฉุกเฉิน (อายุน้อยกว่า 5 ปี)",
      parent: "victim-detail-in-shelter",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_SHELTER"],
    },
  },
  {
    path: "/shelter/:shelterId/victims/:victimId/edit-victim",
    name: "shelter-victims-detail-edit-victim",
    component: () => import("../views/AuthUserRoutes/AddVictim.vue"),
    meta: {
      breadcrumb: "แก้ไขข้อมูลผู้ประสบภัย",
      parent: "victim-detail-in-shelter",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_SHELTER"],
    },
  },
  {
    path: "/shelter/:shelterId/medical-team-schedule",
    name: "medical-team-schedule",
    component: () => import("../views/AuthUserRoutes/MedicalTeamSchedule.vue"),
    meta: {
      breadcrumb: "ตารางทีมแพทย์",
      parent: "shelter-details",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_SHELTER"],
    },
  },
  {
    path: "/shelter/:shelterId/resources",
    name: "resources-in-shelter",
    component: () => import("../views/AuthUserRoutes/ResourcesInShelter.vue"),
    meta: {
      breadcrumb: "ทรัพยากรภายในศูนย์",
      parent: "shelter-details",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_SHELTER"],
    },
  },
  {
    path: "/shelter/:shelterId/resources/request-resources",
    name: "request-resources-in-shelter",
    component: () => import("../views/AuthUserRoutes/AddRequestResources.vue"),
    meta: {
      breadcrumb: "ร้องขอทรัพยากร",
      parent: "resources-in-shelter",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_SHELTER"],
    },
  },
  {
    path: "/shelter/:shelterId/:publicRelationsId",
    name: "public-relations-detail-in-shelter",
    component: () =>
      import("../views/AuthUserRoutes/PublicRelationsDetail.vue"),
    meta: {
      breadcrumb: (route) => decodeURIComponent(route.params.publicRelationsId),
      parent: "shelter-details",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_SHELTER"],
    },
  },
  //menu population
  {
    path: "/population",
    name: "population",
    component: () => import("../views/AuthUserRoutes/Population.vue"),
    meta: {
      // breadcrumb: 'PopulationIcon',
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_POPULATION"],
    },
  },
  {
    path: "/population/victims",
    name: "victims",
    component: () => import("../views/AuthUserRoutes/Victim.vue"),
    meta: {
      breadcrumb: "ผู้ประสบภัย",
      parent: "population",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_POPULATION"],
    },
  },
  {
    path: "/population/victims/add-victim",
    name: "victims-add-victim",
    component: () => import("../views/AuthUserRoutes/AddVictim.vue"),
    meta: {
      breadcrumb: "เพิ่มผู้ประสบภัย",
      parent: "victims",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_POPULATION"],
    },
  },
  {
    path: "/population/victims/:victimId",
    name: "victims-detail",
    component: () => import("../views/AuthUserRoutes/VictimDetails.vue"),
    meta: {
      breadcrumb: (route) => decodeURIComponent(route.params.victimId),
      parent: "victims",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_POPULATION"],
    },
  },
  {
    path: "/population/victims/:victimId/edit-victim",
    name: "victims-edit-victim",
    component: () => import("../views/AuthUserRoutes/AddVictim.vue"),
    meta: {
      breadcrumb: "แก้ไขข้อมูลผู้ประสบภัย",
      parent: "victims-detail",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_POPULATION"],
    },
  },
  {
    path: "/population/victims/:victimId/:populationFormId/:assessmentFormId/form1",
    name: "victims-edit-victim-form1",
    component: () => import("../views/AuthUserRoutes/Form/FormType1.vue"),
    meta: {
      breadcrumb: "รายงานการปฏิบัติการฉุกเฉิน",
      parent: "victims-detail",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_POPULATION"],
    },
  },
  {
    path: "/population/victims/:victimId/:populationFormId/:assessmentFormId/form2",
    name: "victims-edit-victim-form2",
    component: () => import("../views/AuthUserRoutes/Form/FormType2.vue"),
    meta: {
      breadcrumb: "แบบบันทึกเวชระเบียนของทีมปฏิบัติการฉุกเฉิน",
      parent: "victims-detail",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_POPULATION"],
    },
  },
  {
    path: "/population/victims/:victimId/:populationFormId/:assessmentFormId/form3",
    name: "victims-edit-victim-form3",
    component: () =>
      import("../views/AuthUserRoutes/Form/FormType3_backup.vue"),
    meta: {
      breadcrumb:
        "แบบบันทึกเวชระเบียนของทีมปฏิบัติการฉุกเฉิน (อายุน้อยกว่า 5 ปี)",
      parent: "victims-detail",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_POPULATION"],
    },
  },
  {
    path: "/population/post-disaster-period",
    name: "post-disaster-period",
    component: () => import("../views/AuthUserRoutes/PostDisasterPeriod.vue"),
    meta: {
      breadcrumb: "ระยะหลังเกิดภัยพิบัติ",
      parent: "population",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_POPULATION"],
    },
  },
  {
    path: "/population/post-disaster-period/:victimId",
    name: "post-disaster-period-detail",
    component: () =>
      import("../views/AuthUserRoutes/PostDisasterPeriodDetail.vue"),
    meta: {
      breadcrumb: (route) => decodeURIComponent(route.params.victimId),
      parent: "post-disaster-period",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_POPULATION"],
    },
  },

  {
    path: "/population/post-disaster-period/:victimId/edit-victim",
    name: "post-disaster-period-edit-victim",
    component: () => import("../views/AuthUserRoutes/AddVictim.vue"),
    meta: {
      breadcrumb: "แก้ไขข้อมูลผู้ประสบภัย",
      parent: "post-disaster-period-detail",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_POPULATION"],
    },
  },
  {
    path: "/population/post-disaster-period/:victimId/:populationFormId/:assessmentFormId/form1",
    name: "post-disaster-period-detail-form1",
    component: () => import("../views/AuthUserRoutes/Form/FormType1.vue"),
    meta: {
      breadcrumb: "รายงานการปฏิบัติการฉุกเฉิน",
      parent: "post-disaster-period-detail",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_POPULATION"],
    },
  },
  {
    path: "/population/post-disaster-period/:victimId/:populationFormId/:assessmentFormId/form2",
    name: "post-disaster-period-detail-form2",
    component: () => import("../views/AuthUserRoutes/Form/FormType2.vue"),
    meta: {
      breadcrumb: "แบบบันทึกเวชระเบียนของทีมปฏิบัติการฉุกเฉิน",
      parent: "post-disaster-period-detail",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_POPULATION"],
    },
  },
  {
    path: "/population/post-disaster-period/:victimId/:populationFormId/:assessmentFormId/form3",
    name: "post-disaster-period-detail-form3",
    component: () =>
      import("../views/AuthUserRoutes/Form/FormType3_backup.vue"),
    meta: {
      breadcrumb:
        "แบบบันทึกเวชระเบียนของทีมปฏิบัติการฉุกเฉิน (อายุน้อยกว่า 5 ปี)",
      parent: "post-disaster-period-detail",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_POPULATION"],
    },
  },
  {
    path: "/population/total-population",
    name: "total-population",
    component: () => import("../views/AuthUserRoutes/Population.vue"),
    meta: {
      breadcrumb: "ประชากรทั้งหมด",
      parent: "population",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_POPULATION"],
    },
  },
  {
    path: "/population/total-population/add-population",
    name: "add-population",
    component: () => import("../views/AuthUserRoutes/AddPopulation.vue"),
    meta: {
      breadcrumb: "เพิ่มข้อมูลประชากร",
      parent: "total-population",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_POPULATION"],
    },
  },
  {
    path: "/population/total-population/:populationId",
    name: "population-detail",
    component: () => import("../views/AuthUserRoutes/PopulationDetails.vue"),
    meta: {
      breadcrumb: (route) => decodeURIComponent(route.params.populationId),
      parent: "total-population",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_POPULATION"],
    },
  },
  {
    path: "/population/total-population/:populationId/edit-population",
    name: "total-population-edit-population",
    component: () => import("../views/AuthUserRoutes/AddPopulation.vue"),
    meta: {
      breadcrumb: "แก้ไขข้อมูลประชากร",
      parent: "population-detail",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_POPULATION"],
    },
  },
  {
    path: "/population/total-population/:populationId/:populationFormId/:assessmentFormId/form1",
    name: "total-population-edit-population-form1",
    component: () => import("../views/AuthUserRoutes/Form/FormType1.vue"),
    meta: {
      breadcrumb: "รายงานการปฏิบัติการฉุกเฉิน",
      parent: "population-detail",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_POPULATION"],
    },
  },
  {
    path: "/population/total-population/:populationId/:populationFormId/:assessmentFormId/form2",
    name: "total-population-edit-population-form2",
    component: () => import("../views/AuthUserRoutes/Form/FormType2.vue"),
    meta: {
      breadcrumb: "แบบบันทึกเวชระเบียนของทีมปฏิบัติการฉุกเฉิน",
      parent: "population-detail",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_POPULATION"],
    },
  },
  {
    path: "/population/total-population/:populationId/:populationFormId/:assessmentFormId/form3",
    name: "total-population-edit-population-form3",
    component: () =>
      import("../views/AuthUserRoutes/Form/FormType3_backup.vue"),
    meta: {
      breadcrumb:
        "แบบบันทึกเวชระเบียนของทีมปฏิบัติการฉุกเฉิน (อายุน้อยกว่า 5 ปี)",
      parent: "population-detail",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_POPULATION"],
    },
  },
  //menu officer
  {
    path: "/officer",
    name: "officer",
    component: () => import("../views/AuthUserRoutes/Officer.vue"),
    meta: {
      breadcrumb: "รายชื่อเจ้าหน้าที่",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_OFFICER"],
    },
  },
  {
    path: "/officer/add-officer",
    name: "add-officer",
    component: () => import("../views/AuthUserRoutes/AddOfficer.vue"),
    meta: {
      breadcrumb: "เพิ่มบัญชีเจ้าหน้าที่",
      parent: "officer",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_OFFICER"],
    },
  },
  {
    path: "/officer/:officerId",
    name: "officer-detail",
    component: () => import("../views/AuthUserRoutes/OfficerDetails.vue"),
    meta: {
      breadcrumb: (route) => decodeURIComponent(route.params.officerId),
      parent: "officer",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_OFFICER"],
    },
  },
  {
    path: "/officer/:officerId/edit-officer",
    name: "edit-officer",
    component: () => import("../views/AuthUserRoutes/EditOfficer.vue"),
    meta: {
      breadcrumb: "แก้ไขบัญชีเจ้าหน้าที่",
      parent: "officer-detail",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_OFFICER"],
    },
  },
  {
    path: "/pending-approval-list",
    name: "pending-approval-list",
    component: () => import("../views/AuthUserRoutes/PendingApprovalList.vue"),
    meta: {
      breadcrumb: "รายการรออนุมัติ",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_OFFICER"],
    },
  },
  {
    path: "/pending-approval-list/:pendingApprovalListId",
    name: "pending-approval-list-detail",
    component: () =>
      import("../views/AuthUserRoutes/PendingApprovalListDetail.vue"),
    meta: {
      breadcrumb: (route) =>
        decodeURIComponent(route.params.pendingApprovalListId),
      parent: "pending-approval-list",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_OFFICER"],
    },
  },
  {
    path: "/job-position",
    name: "job-position",
    component: () => import("../views/AuthUserRoutes/JobPosition.vue"),
    meta: {
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_OFFICER"],
    },
  },
  {
    path: "/department",
    name: "department",
    component: () => import("../views/AuthUserRoutes/Department.vue"),
    meta: {
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_OFFICER"],
    },
  },
  //menu resource
  {
    path: "/resource",
    name: "resource",
    component: () => import("../views/AuthUserRoutes/Resource.vue"),
    meta: {
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_RESOURCES"],
    },
  },
  //menu public relations
  {
    path: "/public-relations",
    name: "public-relations",
    component: () => import("../views/AuthUserRoutes/PublicRelations.vue"),
    meta: {
      breadcrumb: "บอร์ดประชาสัมพันธ์",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_PUBLIC_REL"],
    },
  },
  {
    path: "/public-relations/:publicRelationsId",
    name: "public-relations-detail",
    component: () =>
      import("../views/AuthUserRoutes/PublicRelationsDetail.vue"),
    meta: {
      breadcrumb: (route) => decodeURIComponent(route.params.publicRelationsId),
      parent: "public-relations",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_PUBLIC_REL"],
    },
  },
  {
    path: "/public-relations-line",
    name: "public-relations-line",
    component: () => import("../views/AuthUserRoutes/Line.vue"),
    meta: {
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_PUBLIC_REL"],
    },
  },
  //menu medical team
  {
    path: "/medical-team",
    name: "medical-team",
    meta: {
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_MEDICAL"],
    },
    children: [
      {
        path: "checkup-schedule",
        meta: {
          requiresAuth: true,
          authUserRoutes: true,
          requirePermsAll: ["VIEW_MEDICAL"],
        },
        component: () =>
          import("../views/AuthUserRoutes/Onsite Team/CheckUpSchedule.vue"),
      },
      {
        path: "team",
        meta: {
          requiresAuth: true,
          authUserRoutes: true,
          requirePermsAll: ["VIEW_MEDICAL"],
        },
        component: () => import("../views/AuthUserRoutes/Onsite Team/Team.vue"),
      },
    ],
  },
  //em
  {
    path: "/emergency-requests/emergency-request-history/:id",
    name: "emergencyRequestsEmergencyRequestHistoryId",
    component: () =>
      import(
        "../views/AuthUserRoutes/RequestEmergency/EmergencyRequestHistoryDetail.vue"
      ),
    meta: {
      breadcrumb: (route) => decodeURIComponent(route.params.id),
      parent: "EmergencyRequestHistory",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_EMERGENCY_REQ"],
    },
  },
  {
    path: "/emergency-requests/emergency-request-history/:id/victim-detail",
    name: "emergencyRequestsEmergencyRequestHistoryIdVictimDetail",
    component: () => import("../views/AuthUserRoutes/VictimDetails.vue"),
    meta: {
      breadcrumb: "ข้อมูลประชากร",
      parent: "emergencyRequestsEmergencyRequestHistoryId",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_EMERGENCY_REQ"],
    },
  },
  {
    path: "/emergency-requests/emergency-request-history/:id/victim-detail/edit-victim",
    name: "emReqHisId-edit-Victim",
    component: () => import("../views/AuthUserRoutes/AddVictim.vue"),
    meta: {
      breadcrumb: "แก้ไขข้อมูลผู้ประสบภัย",
      parent: "emergencyRequestsEmergencyRequestHistoryIdVictimDetail",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_EMERGENCY_REQ"],
    },
  },
  // {
  //   path: "/population/victims/:victimId/edit-victim",
  //   name: "victims-edit-victim",
  //   component: () => import("../views/AuthUserRoutes/AddVictim.vue"),
  //   meta: {
  //     breadcrumb: "แก้ไขข้อมูลผู้ประสบภัย",
  //     parent: "victims-detail",
  //     requiresAuth: true,
  //     authUserRoutes: true,
  //     requirePermsAll: ["VIEW_POPULATION"],
  //   },
  // },
  //menu emergency requests
  {
    path: "/emergency-requests",
    name: "emergencyRequests",
    meta: {
      breadcrumb: "คำร้องขอ",
      requiresAuth: true,
      authUserRoutes: true,
      requirePermsAll: ["VIEW_EMERGENCY_REQ"],
    },
    children: [
      {
        path: "requests",
        meta: {
          requiresAuth: true,
          authUserRoutes: true,
          requirePermsAll: ["VIEW_EMERGENCY_REQ"],
        },
        component: () =>
          import(
            "../views/AuthUserRoutes/RequestEmergency/EmergencyRequests.vue"
          ),
      },
      {
        path: "waiting-lists-for-help",
        meta: {
          requiresAuth: true,
          authUserRoutes: true,
          requirePermsAll: ["VIEW_EMERGENCY_REQ"],
        },
        component: () =>
          import(
            "../views/AuthUserRoutes/RequestEmergency/WaitingListForHelp.vue"
          ),
      },
      {
        path: "movein-victims",
        name: "MoveInVictims",
        meta: {
          requiresAuth: true,
          authUserRoutes: true,
          requirePermsAll: ["VIEW_EMERGENCY_REQ"],
        },
        component: () =>
          import("../views/AuthUserRoutes/RequestEmergency/MoveInVictims.vue"),
      },
      {
        path: "add-emergency",
        name: "AddEmergency",
        meta: {
          requiresAuth: true,
          authUserRoutes: true,
          requirePermsAll: ["VIEW_EMERGENCY_REQ"],
        },
        component: () =>
          import("../views/AuthUserRoutes/RequestEmergency/AddEmergency.vue"),
      },
      {
        path: "emergency-request-history",
        name: "EmergencyRequestHistory",
        meta: {
          breadcrumb: "ประวัติคำขอที่ สำเร็จ/ปฎิเสธ/ล้มเหลว",
          parent: "emergencyRequests",
          requiresAuth: true,
          authUserRoutes: true,
          requirePermsAll: ["VIEW_EMERGENCY_REQ"],
        },
        component: () =>
          import(
            "../views/AuthUserRoutes/RequestEmergency/EmergencyRequestHistory.vue"
          ),
      },
      // {
      //   path: "emergency-request-history/:id",
      //   meta: {
      //     breadcrumb: (route) => decodeURIComponent(route.params.id),
      //           parent: "EmergencyRequestHistory",
      //     requiresAuth: true,
      //     authUserRoutes: true,
      //     requirePermsAll: ["VIEW_EMERGENCY_REQ"],
      //   },
      //   component: () =>
      //     import(
      //       "../views/AuthUserRoutes/RequestEmergency/EmergencyRequestHistoryDetail.vue"
      //     ),
      // },
      {
        path: "reporter-detail/:id",
        name: "ReporterDetail",
        meta: {
          requiresAuth: true,
          authUserRoutes: true,
          requirePermsAll: ["VIEW_EMERGENCY_REQ"],
        },
        component: () =>
          import("../views/AuthUserRoutes/RequestEmergency/ReporterDetail.vue"),
      },
      {
        path: "waiting-lists-reporter-detail/:id",
        name: "WaitingListReporterDetail",
        meta: {
          requiresAuth: true,
          authUserRoutes: true,
          requirePermsAll: ["VIEW_EMERGENCY_REQ"],
        },
        component: () =>
          import(
            "../views/AuthUserRoutes/RequestEmergency/WaitingListReporterDetail.vue"
          ),
      },
      
    ],
  },
  //
  {
    path: "/follow-up-after-disaster",
    component: () => import("../views/AuthUserRoutes/AfterDisaster.vue"),
    meta: {
      requiresAuth: true,
      authUserRoutes: true,
    },
  },
  {
    path: "/offline-assessment",
    name: "offline-assessment",
    component: OfflineAssessment,
    meta: {
      breadcrumb: "แบบประเมินออฟไลน์",
      requiresAuth: true,
      authUserRoutes: true,
    },
  },
   {
    path: "/offline-assessment/:victimId/:populationFormId/:assessmentFormId/form2",
    name: "offline-post-disaster-period-detail-form2",
    component: FormType2,
    meta: {
      breadcrumb: "แบบบันทึกเวชระเบียนของทีมปฏิบัติการฉุกเฉิน",
      parent: "offline-assessment",
      requiresAuth: true,
      authUserRoutes: true,
    },
  },
   { 
    path: "/offline-assessment/:victimId/:populationFormId/:assessmentFormId/form1",
    name: "offline-post-disaster-period-detail-form1",
    component: FormType1,
    meta: {
      breadcrumb: "รายงานการปฏิบัติการฉุกเฉิน",
      parent: "offline-assessment",
      requiresAuth: true,
      authUserRoutes: true,
    }
  },
  {
    path: "/population/total-population/:populationFormId/add-population-newinfo",
    name: "add-population-newinfo",
    component: () => import("../views/AuthUserRoutes/AddPopulation.vue"),
    meta: {
      breadcrumb: "เพิ่มข้อมูลใหม่",
      parent: "offline-assessment",
      requiresAuth: true,
      authUserRoutes: true,
      // requirePermsAll: ["VIEW_POPULATION"],
    },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("../views/NotFound.vue"),
  },
];
const router = createRouter({
  // linkActiveClass: '!text-primary',
  // linkExactActiveClass: '!text-primary',
  history: createWebHistory(),
  routes,
});

// router.beforeEach(async (to, from, next) => {

//   const needAll = to.matched.flatMap(r => r.meta?.requirePermsAll ?? []);
//   const anyGroups = to.matched.map(r => r.meta?.requirePermsAny).filter(Boolean);
//   const hasAll = needAll.every(p => auth.can(p));
//   const hasAnyGroups = anyGroups.every(group => group.some(p => auth.can(p)));

// });

const slugify = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .slice(0, 80);

router.beforeEach(async (to, from, next) => {
  if (to.path === "/") return next("/home-page");

  const token = VueCookies.get("token");
  const browserOnline = navigator.onLine;

  // ตอน offline ให้ค้างหน้า home-page ไว้ก่อน (ไม่ redirect ไป dashboard)
  if (token && to.path === "/home-page" && browserOnline) {
    return next({ name: "dashboard", replace: true });
  }

  const guestOnly = ["/login", "/register", "/forgot-password"];
  if (token && guestOnly.includes(to.path) && browserOnline) {
    return next("/dashboard");
  }

  const requiresAuth = to.matched.some(
    (r) => r.meta?.authUserRoutes || r.meta?.requiresAuth
  );
  if (!requiresAuth) return next();

  if (!token) return next("/login");

  const auth = useAuthStore();
  try {
    if (!auth.userData) {
      await auth.getProfile();
    }
  } catch (e) {
    return next("/login");
  }

  if (to.name === "shelter" && auth.hasRole && auth.hasRole([ROLES.staff])) {
    const ud = auth.userData || {};
    // ใช้ชื่อ หรือ id ที่ระบบคุณมีจริง (เลือกอันที่ backend รองรับ)
    const shelterId =
      ud.shelter_id != null ? String(ud.shelter_id) : slugify(ud.shelter_name);

    if (shelterId) {
      // กัน redirect loop: ถ้าเดิมทีก็อยู่หน้า details อยู่แล้ว ไม่ต้อง next อีก
      if (
        !(
          from.name === "shelter-details" &&
          from.params?.shelterId === shelterId
        )
      ) {
        return next({ name: "shelter-details", params: { shelterId } });
      }
    } else {
      // ถ้าไม่รู้ว่าศูนย์ไหน ให้กันไว้กลับ dashboard
      return next("/dashboard");
    }
  }

  const needAll = to.matched.flatMap((r) => r.meta?.requirePermsAll ?? []);
  const anyGroups = to.matched
    .map((r) => r.meta?.requirePermsAny)
    .filter(Boolean);
  const hasAll = needAll.length === 0 || needAll.every((p) => auth.can(p));
  const hasAnyGroups =
    anyGroups.length === 0 ||
    anyGroups.every((group) => group.some((p) => auth.can(p)));

  if (!hasAll || !hasAnyGroups) {
    return next("/dashboard");
  }

  return next();
});

const allowedKeys = [
  "phone",
  "shelterId",
  "victimId",
  "populationId",
  "officerId",
  "pendingApprovalListId",
  "publicRelationsId",
  "caregiverId",
];

router.beforeResolve((to) => {
  // 1) encode URL segment ถ้ายังไม่ encode (ของเดิมคุณมีอยู่แล้ว)
  const newParams = {};
  let needRedirect = false;
  for (const key of allowedKeys) {
    if (to.params[key]) {
      const raw = decodeURIComponent(String(to.params[key]));
      if (!isB64u(raw)) {
        newParams[key] = b64uEncode(raw);
        needRedirect = true;
      }
    }
  }
  if (needRedirect) {
    return {
      name: to.name,
      params: { ...to.params, ...newParams },
      query: to.query,
      hash: to.hash,
      replace: true,
    };
  }

  // 2) ทำสำเนา params ที่ "ถอดแล้ว" เก็บใน meta
  const decoded = { ...to.params };
  for (const key of allowedKeys) {
    const v = decoded[key];
    if (v == null) continue;
    if (Array.isArray(v)) {
      decoded[key] = v.map((x) => (isB64u(x) ? b64uDecode(x) : x));
    } else {
      decoded[key] = isB64u(v) ? b64uDecode(v) : v;
    }
  }
  // meta เป็น object ที่เราติดข้อมูลเพิ่มได้
  to.meta.decodedParams = decoded;

  return true;
});

export default router;
